import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, useNavigate } from 'react-router-dom';
import { useFindError } from 'hooks/useFindError';
import { useForm } from 'react-hook-form';
import { signInScheme } from 'validationSchemes';
import { Images } from 'assets/images';
import { authActions, httpRequestsOnErrorsActions, settingsActions } from 'store';
import { INPUT_PASSWORD_TYPE, INPUT_TEXT_TYPE } from 'utils/constant';
import { ROUTES } from 'utils/routeNames';
import { SocialLogin } from 'components/socialLogin';
import { LoadingButton } from 'components';
import { useFindLoad } from 'hooks/useFindLoad';
import { GET_USER_INFO, LOG_IN } from 'store/auth';
import { setStorage } from 'utils/storageHelpers';
import { ImageLazy } from 'components/ImageLazy';
import { EditEmailModal } from 'components/editEmailModal/editEmailModal';
import { LazyContainer } from 'components/ImageLazy/styled';
import {
  InputBlock,
  PasswordVisibleIcon,
  StyledInput,
} from 'components/inputs/inputStyles';
import {
  Wrapper,
  LoginFormContainer,
  TextLogin,
  CloseIcon,
  Form,
  SuggestionBlockWithIcons,
  RememberAndForgotSection,
  SignUpText,
  ForgotText,
  SuggestionText,
  TextBold,
  LoginErrorMessage,
  CloseIconLink,
  StyledButtonContainer,
  AuthenticationLink,
  //  TODO
  // CheckboxLabel,
  // RememberText,
  // CheckboxInput,
} from './styled';

const Login = () => {
  const { user } = useSelector(({ auth }) => auth);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(signInScheme),
  });
  const dispatch = useDispatch();

  const loginError = useFindError(LOG_IN);

  const isLoading = useFindLoad(LOG_IN);
  const userInfoError = useFindError(GET_USER_INFO);

  const accessToken = useSelector((state) => state.auth.accessToken);

  const [inputType, setInputType] = useState('password');
  const [showEmailModal, setShowEmailModal] = useState(false);

  const togglePasswordVisibility = () => {
    setInputType((prev) =>
      prev === INPUT_PASSWORD_TYPE ? INPUT_TEXT_TYPE : INPUT_PASSWORD_TYPE,
    );
  };

  useEffect(() => {
    dispatch(httpRequestsOnErrorsActions.removeError(LOG_IN));
  }, []);

  useEffect(() => {
    dispatch(httpRequestsOnErrorsActions.removeError(LOG_IN));
  }, [watch('email'), watch('password')]);

  useEffect(() => {
    if (accessToken && user) {
      navigate(ROUTES.HOME);
    }
    const formValues = getValues();

    if (accessToken && userInfoError[0]?.error?.verified === false) {
      setStorage('email', formValues.email);
      dispatch(httpRequestsOnErrorsActions.removeError(GET_USER_INFO));
      navigate(ROUTES.VERIFY_EMAIL);
    }
  }, [accessToken, user, userInfoError, navigate]);

  const onSubmit = (data) => {
    dispatch(settingsActions.getCountries());

    if (data.email && data.password) {
      dispatch(
        authActions.logIn({
          username: data.email,
          password: data.password,
        }),
      );
    }
  };

  return (
    <Wrapper>
      <LazyContainer>
        <ImageLazy
          src={Images.loginMainBackground}
          width="100%"
          height="100%"
          hash="LqBE%hJDxWWBX=xaaxa}x^awWYof"
        />
      </LazyContainer>

      <LoginFormContainer>
        <Form onSubmit={handleSubmit(onSubmit)} autoComplete={false}>
          <TextLogin>Sign In</TextLogin>
          <CloseIconLink to={ROUTES.INDEX}>
            <CloseIcon src={Images.closeBlueIcon} />
          </CloseIconLink>

          <InputBlock emailAddress>
            <StyledInput
              emailAddress
              type="text"
              placeholder="Email Address"
              {...register('email')}
              error={errors?.email?.message || loginError}
            />
          </InputBlock>

          <LoginErrorMessage>{errors?.email?.message}</LoginErrorMessage>

          <InputBlock emailAddress>
            <StyledInput
              emailAddress
              type={inputType}
              placeholder="Password"
              {...register('password')}
              error={errors?.password?.message || loginError}
            />
            <PasswordVisibleIcon
              src={
                inputType === INPUT_PASSWORD_TYPE
                  ? Images.passwordClosedEye
                  : Images.passwordOpenedEye
              }
              onClick={togglePasswordVisibility}
            />
            {errors?.password?.message ? (
              <LoginErrorMessage>{errors?.password?.message}</LoginErrorMessage>
            ) : loginError?.[0]?.error.error ? (
              <LoginErrorMessage>
                Incorrect Email Address or Password
              </LoginErrorMessage>
            ) : null}
          </InputBlock>

          <RememberAndForgotSection>
            {/* TODO */}
            {/*<CheckboxLabel>*/}
            {/*  <CheckboxInput type="checkbox" />*/}
            {/*  <RememberText>Remember Me</RememberText>*/}
            {/*</CheckboxLabel>*/}

            <Link to={ROUTES.FORGOT_PASSWORD}>
              <ForgotText>Forgot Password?</ForgotText>
            </Link>
          </RememberAndForgotSection>

          <StyledButtonContainer>
            <LoadingButton
              width="100%"
              height="54px"
              fs="20px"
              loading={isLoading}
              name="Sign In"
              type="submit"
            />
          </StyledButtonContainer>
        </Form>

        <SuggestionBlockWithIcons>
          <TextBold>Or Sign In with</TextBold>
          <SocialLogin setShowEmailModal={setShowEmailModal} />
          <SuggestionText>
            <SignUpText>Don`t have an account?</SignUpText>
            <AuthenticationLink to={ROUTES.REGISTER}>Sign Up</AuthenticationLink>
          </SuggestionText>
        </SuggestionBlockWithIcons>
      </LoginFormContainer>
      {showEmailModal ? (
        <EditEmailModal setShowEmailModal={setShowEmailModal} />
      ) : null}
    </Wrapper>
  );
};

export default Login;
